import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import DataTable from '../common/DataTable';
import { formatCurrency, formatDate, getAgeing, today, firstOfMonth } from '../../utils/helpers';

// Item weight (Kg) × quantity, shown on the item/party/HSN sales reports.
const fmtWt = (v) => `${(Number(v) || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })} kg`;

const reportTypes = [
  { key: 'register', label: 'Sales Register' },
  { key: 'partywise', label: 'Party-wise Sales' },
  { key: 'itemwise', label: 'Item-wise Sales' },
  { key: 'gst', label: 'GST Sales Register' },
  { key: 'itemHsn', label: 'Sales Item Wise HSN Report' },
  { key: 'receiptAdj', label: 'Receipts Adjustment Report' },
  { key: 'receivables', label: 'Outstanding Receivables' },
  { key: 'returns', label: 'Sales Return Register' },
];

export default function SalesReports() {
  const { state } = useApp();
  const [report, setReport] = useState('register');
  const [startDate, setStartDate] = useState(firstOfMonth());
  const [endDate, setEndDate] = useState(today());

  const filtered = useMemo(() => {
    let inv = state.salesInvoices;
    if (startDate) inv = inv.filter(i => i.date >= startDate);
    if (endDate) inv = inv.filter(i => i.date <= endDate);
    return inv;
  }, [state.salesInvoices, startDate, endDate]);

  // Enrich each invoice with resolved customer name + total qty/weight, so the
  // on-screen table and the Excel/PDF export both show readable values.
  const registerData = useMemo(() => filtered.map(inv => {
    let qty = 0, weight = 0;
    (inv.items || []).forEach(li => {
      const item = state.items.find(i => i.id === li.itemId);
      qty += Number(li.qty) || 0;
      weight += (Number(li.qty) || 0) * (Number(item?.weight) || 0);
    });
    const party = state.parties.find(p => p.id === inv.partyId);
    return {
      ...inv,
      customerName: party?.name || '-',
      customerGstin: party?.gstin || '-',
      customerState: party?.state || '-',
      customerStateCode: party?.stateGstCode || '-',
      qty, weight,
    };
  }), [filtered, state.parties, state.items]);

  const registerCols = [
    { key: 'invoiceNo', label: 'Invoice No', sortable: true },
    { key: 'date', label: 'Date', render: (v) => formatDate(v), exportValue: (r) => formatDate(r.date) },
    { key: 'customerName', label: 'Customer', sortable: true },
    { key: 'customerGstin', label: 'GST No' },
    { key: 'customerState', label: 'State' },
    { key: 'customerStateCode', label: 'State Code' },
    { key: 'qty', label: 'Qty', align: 'right', sortable: true },
    { key: 'weight', label: 'Weight (Kg)', align: 'right', sortable: true, render: (v) => fmtWt(v), exportValue: (r) => r.weight },
    { key: 'taxable', label: 'Taxable', align: 'right', render: (v) => formatCurrency(v) },
    { key: 'totalTax', label: 'Tax', align: 'right', render: (v) => formatCurrency(v) },
    { key: 'grandTotal', label: 'Total', align: 'right', sortable: true, render: (v) => formatCurrency(v) },
    { key: 'status', label: 'Status', render: (v) => <span className={`badge ${v === 'Paid' ? 'badge-success' : 'badge-danger'}`}>{v}</span> },
  ];

  const partywiseData = useMemo(() => {
    const map = {};
    filtered.forEach(inv => {
      const name = state.parties.find(p => p.id === inv.partyId)?.name || 'Unknown';
      if (!map[name]) map[name] = { party: name, count: 0, weight: 0, total: 0 };
      map[name].count++;
      map[name].total += inv.grandTotal || 0;
      (inv.items || []).forEach(li => {
        const item = state.items.find(i => i.id === li.itemId);
        map[name].weight += (Number(li.qty) || 0) * (Number(item?.weight) || 0);
      });
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filtered, state.parties, state.items]);

  const itemwiseData = useMemo(() => {
    const map = {};
    filtered.forEach(inv => {
      inv.items?.forEach(li => {
        const item = state.items.find(i => i.id === li.itemId);
        const name = item?.name || 'Unknown';
        if (!map[name]) map[name] = { item: name, qty: 0, weight: 0, total: 0 };
        map[name].qty += Number(li.qty) || 0;
        map[name].weight += (Number(li.qty) || 0) * (Number(item?.weight) || 0);
        map[name].total += li.netAmount || 0;
      });
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filtered, state.items]);

  const gstData = useMemo(() => filtered.map(inv => ({
    ...inv,
    customerName: state.parties.find(p => p.id === inv.partyId)?.name,
    customerGstin: state.parties.find(p => p.id === inv.partyId)?.gstin,
  })), [filtered, state.parties]);

  const receivablesData = useMemo(() => {
    return state.salesInvoices
      .filter(i => i.status !== 'Paid')
      .map(inv => ({
        ...inv,
        customerName: state.parties.find(p => p.id === inv.partyId)?.name,
        outstanding: (inv.grandTotal || 0) - (inv.paidAmount || 0),
        ageing: getAgeing(inv.date),
      }));
  }, [state.salesInvoices, state.parties]);

  const returnsCols = [
    { key: 'returnNo', label: 'Return No' },
    { key: 'date', label: 'Date', render: (v) => formatDate(v), exportValue: (r) => formatDate(r.date) },
    { key: 'partyId', label: 'Customer', render: (v) => state.parties.find(p => p.id === v)?.name, exportValue: (r) => state.parties.find(p => p.id === r.partyId)?.name || '' },
    { key: 'againstInvoice', label: 'Against Invoice' },
    { key: 'grandTotal', label: 'Amount', align: 'right', render: (v) => formatCurrency(v) },
  ];

  const renderReport = () => {
    switch (report) {
      case 'register':
        return <DataTable columns={registerCols} data={registerData} searchFields={['invoiceNo', 'customerName']}
          totals={(rows) => ({
            qty: rows.reduce((s, r) => s + r.qty, 0),
            weight: fmtWt(rows.reduce((s, r) => s + r.weight, 0)),
            taxable: formatCurrency(rows.reduce((s, r) => s + (r.taxable || 0), 0)),
            grandTotal: formatCurrency(rows.reduce((s, r) => s + (r.grandTotal || 0), 0)),
          })} />;
      case 'partywise':
        return <DataTable columns={[
          { key: 'party', label: 'Party', sortable: true },
          { key: 'count', label: 'Invoices', align: 'right' },
          { key: 'weight', label: 'Weight (Kg)', align: 'right', sortable: true, render: (v) => fmtWt(v), exportValue: (r) => r.weight },
          { key: 'total', label: 'Total', align: 'right', sortable: true, render: (v) => formatCurrency(v) },
        ]} data={partywiseData} searchFields={['party']}
          totals={(rows) => ({
            count: rows.reduce((s, r) => s + r.count, 0),
            weight: fmtWt(rows.reduce((s, r) => s + r.weight, 0)),
            total: formatCurrency(rows.reduce((s, r) => s + r.total, 0)),
          })} />;
      case 'itemwise':
        return <DataTable columns={[
          { key: 'item', label: 'Item', sortable: true },
          { key: 'qty', label: 'Qty Sold', align: 'right', sortable: true },
          { key: 'weight', label: 'Weight (Kg)', align: 'right', sortable: true, render: (v) => fmtWt(v), exportValue: (r) => r.weight },
          { key: 'total', label: 'Total', align: 'right', sortable: true, render: (v) => formatCurrency(v) },
        ]} data={itemwiseData} searchFields={['item']}
          totals={(rows) => ({
            qty: rows.reduce((s, r) => s + r.qty, 0),
            weight: fmtWt(rows.reduce((s, r) => s + r.weight, 0)),
            total: formatCurrency(rows.reduce((s, r) => s + r.total, 0)),
          })} />;
      case 'gst':
        return <DataTable columns={[
          { key: 'invoiceNo', label: 'Invoice No' },
          { key: 'date', label: 'Date', render: (v) => formatDate(v), exportValue: (r) => formatDate(r.date) },
          { key: 'customerName', label: 'Customer' },
          { key: 'customerGstin', label: 'GSTIN' },
          { key: 'taxable', label: 'Taxable', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'cgst', label: 'CGST', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'sgst', label: 'SGST', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'igst', label: 'IGST', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'grandTotal', label: 'Total', align: 'right', render: (v) => formatCurrency(v) },
        ]} data={gstData} searchFields={['invoiceNo', 'customerName']} />;
      case 'itemHsn': {
        const rows = [];
        filtered.forEach(inv => {
          (inv.items || []).forEach(li => {
            const item = state.items.find(i => i.id === li.itemId);
            const taxAmt = Number(li.taxAmount) || 0;
            const inter = inv.isInterState;
            rows.push({
              itemName: item?.name || li.itemName || 'Unknown',
              invoiceDate: inv.date,
              invoiceNo: inv.invoiceNo,
              hsn: item?.hsnCode || '-',
              qty: Number(li.qty) || 0,
              uom: item?.unit || li.unit || '-',
              weight: (Number(li.qty) || 0) * (Number(item?.weight) || 0),
              taxable: Number(li.taxableAmount) || 0,
              sgst: inter ? 0 : taxAmt / 2,
              cgst: inter ? 0 : taxAmt / 2,
              igst: inter ? taxAmt : 0,
              total: Number(li.netAmount) || 0,
            });
          });
        });
        return <DataTable columns={[
          { key: 'itemName', label: 'Item Name', sortable: true },
          { key: 'invoiceDate', label: 'Invoice Date', render: (v) => v ? formatDate(v) : '-', exportValue: (r) => r.invoiceDate ? formatDate(r.invoiceDate) : '' },
          { key: 'invoiceNo', label: 'Invoice No' },
          { key: 'hsn', label: 'HSN No' },
          { key: 'qty', label: 'Qty', align: 'right' },
          { key: 'uom', label: 'UOM' },
          { key: 'weight', label: 'Weight (Kg)', align: 'right', render: (v) => fmtWt(v), exportValue: (r) => r.weight },
          { key: 'taxable', label: 'Taxable Amount', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'sgst', label: 'SGST', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'cgst', label: 'CGST', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'igst', label: 'IGST', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'total', label: 'Total', align: 'right', render: (v) => formatCurrency(v) },
        ]} data={rows} searchFields={['itemName', 'invoiceNo', 'hsn']} />;
      }

      case 'receiptAdj': {
        const data = state.receipts
          .filter(r => r.againstInvoice)
          .filter(r => (!startDate || r.date >= startDate) && (!endDate || r.date <= endDate))
          .map(r => {
            const inv = state.salesInvoices.find(i => i.invoiceNo === r.againstInvoice);
            return { receiptNo: r.receiptNo, date: r.date, invoiceNo: r.againstInvoice, invoiceDate: inv?.date || '', amount: r.amount };
          });
        return <DataTable columns={[
          { key: 'receiptNo', label: 'Receipt Entry No', sortable: true },
          { key: 'date', label: 'Date', render: (v) => formatDate(v), exportValue: (r) => formatDate(r.date) },
          { key: 'invoiceNo', label: 'Invoice No' },
          { key: 'invoiceDate', label: 'Invoice Date', render: (v) => v ? formatDate(v) : '-', exportValue: (r) => r.invoiceDate ? formatDate(r.invoiceDate) : '' },
          { key: 'amount', label: 'Amount', align: 'right', sortable: true, render: (v) => formatCurrency(v) },
        ]} data={data} searchFields={['receiptNo', 'invoiceNo']} />;
      }

      case 'receivables':
        return <DataTable columns={[
          { key: 'invoiceNo', label: 'Invoice No' },
          { key: 'date', label: 'Date', render: (v) => formatDate(v), exportValue: (r) => formatDate(r.date) },
          { key: 'customerName', label: 'Customer' },
          { key: 'grandTotal', label: 'Invoice Amt', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'paidAmount', label: 'Paid', align: 'right', render: (v) => formatCurrency(v || 0) },
          { key: 'outstanding', label: 'Outstanding', align: 'right', render: (v) => <span className="text-red-600 font-semibold">{formatCurrency(v)}</span> },
          { key: 'ageing', label: 'Ageing', render: (v) => (
            <span className={`badge ${v === '0-30' ? 'badge-success' : v === '31-60' ? 'badge-warning' : 'badge-danger'}`}>{v} days</span>
          ), exportValue: (r) => `${r.ageing} days` },
        ]} data={receivablesData} searchFields={['invoiceNo', 'customerName']} />;
      case 'returns':
        return <DataTable columns={returnsCols} data={state.salesReturns} searchFields={['returnNo']} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <h1 className="page-title mb-6">Sales Reports</h1>
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="label">Report Type</label>
            <select className="input" value={report} onChange={e => setReport(e.target.value)}>
              {reportTypes.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">From</label>
            <input className="input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input className="input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>
      </div>
      <div className="card">{(() => {
        const el = renderReport();
        return el && el.type === DataTable ? React.cloneElement(el, { exportFilename: `Sales_${report}` }) : el;
      })()}</div>
    </div>
  );
}
